
import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon, Fuel, Camera } from 'lucide-react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { FuelType, Vehicle } from '@/hooks/vehicle-maintenance/types';
import { FuelReceiptUploadModal } from './FuelReceiptUploadModal';
import { FuelReceiptData } from '@/services/fuel-receipt-parser';
import { FuelFormLoadingSkeleton } from './FuelFormLoadingSkeleton';
import { NoVehiclesState } from './VehicleLoadingState';

const fuelTypes: FuelType[] = [
  "diesel",
  "petrol",
  "electric",
  "hybrid",
  "biodiesel",
  "ethanol",
  "lpg",
  "other"
];

const currencies = [
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "JPY",
  "CNY"
];

// Define the shape of our form values
export type FuelLogFormValues = {
  vehicle_id: string;
  odometer: number;
  fill_date: Date;
  fuel_amount: number;
  fuel_price_per_unit: number;
  total_cost: number;
  fuel_type: string;
  fuel_station?: string;
  currency: string;
  notes?: string;
  full_tank: boolean;
};

// Create a Zod schema for validation
const fuelLogSchema = z.object({
  vehicle_id: z.string().uuid(),
  odometer: z.number().positive(),
  fill_date: z.date(),
  fuel_amount: z.number().positive(),
  fuel_price_per_unit: z.number().nonnegative(), // Calculated, can be 0 initially
  total_cost: z.number().positive(),
  fuel_type: z.string(),
  fuel_station: z.string().optional(),
  currency: z.string(),
  notes: z.string().optional(),
  full_tank: z.boolean().default(true)
});

interface FuelLogFormProps {
  onSubmit: (data: FuelLogFormValues) => Promise<void>;
  vehicles: Vehicle[];
  isLoadingVehicles?: boolean;
  initialValues?: Partial<FuelLogFormValues>;
  isUpdate?: boolean;
  onCancel: () => void;
}

const FuelLogForm = ({ onSubmit, vehicles, isLoadingVehicles = false, initialValues, isUpdate = false, onCancel }: FuelLogFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const form = useForm<FuelLogFormValues>({
    resolver: zodResolver(fuelLogSchema),
    defaultValues: {
      vehicle_id: initialValues?.vehicle_id || (vehicles.length === 1 ? vehicles[0].id : ''),
      odometer: initialValues?.odometer || 0,
      fill_date: initialValues?.fill_date || new Date(),
      fuel_amount: initialValues?.fuel_amount || 0,
      fuel_price_per_unit: initialValues?.fuel_price_per_unit || 0,
      total_cost: initialValues?.total_cost || 0,
      fuel_type: initialValues?.fuel_type || 'diesel',
      fuel_station: initialValues?.fuel_station || '',
      currency: initialValues?.currency || 'AUD', // Default to AUD for Australian users
      notes: initialValues?.notes || '',
      full_tank: initialValues?.full_tank !== undefined ? initialValues.full_tank : true
    }
  });

  const watchFuelAmount = form.watch('fuel_amount');
  const watchTotalCost = form.watch('total_cost');

  // Calculate price per unit from total cost and fuel amount
  const handleCalculatePricePerUnit = (amount?: number, cost?: number) => {
    const a = amount ?? watchFuelAmount;
    const c = cost ?? watchTotalCost;

    if (a > 0 && c > 0) {
      const pricePerUnit = Number((c / a).toFixed(3));
      form.setValue('fuel_price_per_unit', pricePerUnit, { shouldValidate: true });
    }
  };

  const handleFinalSubmit = async (data: FuelLogFormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhotoUpload = () => {
    setShowUploadModal(true);
  };

  const handleReceiptProcessed = (fuelData: FuelReceiptData, vehicleId: string) => {
    // Convert receipt data to form values and populate the form
    // Only set vehicle_id if user has multiple vehicles, otherwise it's already set
    if (vehicles.length > 1) {
      form.setValue('vehicle_id', vehicleId);
    }
    form.setValue('fill_date', new Date(fuelData.date));
    form.setValue('odometer', fuelData.odometerReading || 0);
    form.setValue('fuel_amount', fuelData.combinedTotals.totalVolume);
    form.setValue('total_cost', fuelData.combinedTotals.totalAmount);
    form.setValue('fuel_price_per_unit', fuelData.combinedTotals.blendedPrice);
    form.setValue('fuel_station', fuelData.stationName);
    form.setValue('fuel_type', 'diesel'); // Default, user can change if needed
    form.setValue('full_tank', true); // Assume full tank for dual tank receipts

    // Close modal - form is now populated and ready for review/editing
    setShowUploadModal(false);
  };

  // Show loading skeleton while vehicles are loading
  if (isLoadingVehicles) {
    return <FuelFormLoadingSkeleton />;
  }

  // Show no vehicles state if no vehicles after loading
  if (!isLoadingVehicles && (!vehicles || vehicles.length === 0)) {
    return <NoVehiclesState onAddVehicle={() => window.location.href = '/profile?tab=vehicles'} />;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fuel className="h-5 w-5" />
            {isUpdate ? 'Update Fuel Log' : 'Add Fuel Log'}
          </CardTitle>
          <div className="flex items-center justify-between">
            <CardDescription>
              Keep track of your fuel consumption and costs
            </CardDescription>
            {!isUpdate && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePhotoUpload}
                className="flex items-center gap-2"
              >
                <Camera className="h-4 w-4" />
                Upload Receipt
              </Button>
            )}
          </div>
        </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleFinalSubmit)}>
          <CardContent className="space-y-4">
            {/* Vehicle selection - only show if multiple vehicles */}
            {vehicles.length > 1 && (
              <FormField
                control={form.control}
                name="vehicle_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vehicle</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isUpdate}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a vehicle" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {vehicles.map(vehicle => (
                          <SelectItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.name} ({vehicle.model} {vehicle.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Show current vehicle name when only one vehicle */}
            {vehicles.length === 1 && (
              <div className="space-y-2">
                <FormLabel>Vehicle</FormLabel>
                <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                  <Fuel className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {vehicles[0].name} ({vehicles[0].model} {vehicles[0].year})
                  </span>
                </div>
              </div>
            )}

            {/* Date picker */}
            <FormField
              control={form.control}
              name="fill_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Fill Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className="pl-3 text-left font-normal"
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Odometer reading */}
            <FormField
              control={form.control}
              name="odometer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Odometer Reading</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Fuel type selection */}
            <FormField
              control={form.control}
              name="fuel_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fuel Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select fuel type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {fuelTypes.map(type => (
                        <SelectItem key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Fuel amount */}
            <FormField
              control={form.control}
              name="fuel_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fuel Amount (Liters)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 65.5"
                      {...field}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        field.onChange(val);
                        handleCalculatePricePerUnit(val, watchTotalCost);
                      }}
                    />
                  </FormControl>
                  <FormDescription>How many liters did you put in?</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Total cost - user enters this */}
            <FormField
              control={form.control}
              name="total_cost"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Cost (AUD)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g. 125.50"
                      {...field}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        field.onChange(val);
                        handleCalculatePricePerUnit(watchFuelAmount, val);
                      }}
                    />
                  </FormControl>
                  <FormDescription>Total amount you paid</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Price per unit - calculated automatically */}
            {watchFuelAmount > 0 && watchTotalCost > 0 && (
              <div className="p-3 rounded-lg bg-muted/50 border">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Price per Liter</span>
                  <span className="font-semibold text-lg">
                    ${(watchTotalCost / watchFuelAmount).toFixed(3)} AUD/L
                  </span>
                </div>
              </div>
            )}

            {/* Hidden currency field - defaults to AUD */}
            <input type="hidden" {...form.register('currency')} />
            <input type="hidden" {...form.register('fuel_price_per_unit')} />

            {/* Fuel station */}
            <FormField
              control={form.control}
              name="fuel_station"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fuel Station (Optional)</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Full tank checkbox */}
            <FormField
              control={form.control}
              name="full_tank"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Full Tank</FormLabel>
                    <FormDescription>
                      Check if you filled the tank completely
                    </FormDescription>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : isUpdate ? 'Update Log' : 'Add Log'}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>

    {/* Upload Modal */}
    <FuelReceiptUploadModal
      open={showUploadModal}
      onOpenChange={setShowUploadModal}
      vehicles={vehicles}
      onReceiptProcessed={handleReceiptProcessed}
    />
    </>
  );
};

export default FuelLogForm;
